import {
    Button,
    Card,
    CardContent,
    CardHeader,
    Checkbox,
    Chip,
    colors,
    Grid,
    InputAdornment,
    makeStyles,
    Switch,
    TextField,
    Theme,
    Typography
} from '@material-ui/core';
import {useSnackbar} from 'notistack';
import React, {useEffect, useState} from 'react';
import validate from 'validate.js';
import {Can} from '../../../components/Can';
import ConfirmDelete from '../../../components/ConfirmDelete';
import {LoadingOverlay} from '../../../components/LoadingOverlay/LoadingOverlay';
import anyLoading from '../../../utils/anyLoading';
import {getHandleFieldChange, getHasError} from '../../../utils/formHelpers';
import createPayment, {CreatePaymentInput} from './createPayment.api';
import updatePayment, {UpdatePaymentInput} from './updatePayment.api';
import deletePayment from './deletePayment.api';
import {Payment, PaymentStatus, PaymentStatuses, PaymentType, PaymentTypes} from '../../../graphql/payments.datatypes';
import clsx from 'clsx';
import getBillingParty from '../../BillingParty/getBillingParty.api';
import moment from 'moment';
import {red} from '@material-ui/core/colors';
import BlockOutlinedIcon from '@material-ui/icons/BlockOutlined';
import useRouter from 'use-react-router';
import {AdjustmentType, GroupMember} from '../../../graphql/groups.datatypes';
import ConfirmModal from '../../../components/ConfirmModal';

const useStyles = makeStyles((theme: Theme) => ({
    container: {
        marginTop: theme.spacing(3),
        width: '100%',
        marginBottom: theme.spacing(2),
    },
    card: {},
    saveButton: {
        marginLeft: theme.spacing(1),
        color: theme.palette.white,
        backgroundColor: colors.green[600],
        '&:hover': {
            backgroundColor: colors.green[900]
        }
    },
    deleteButton: {
        marginLeft: theme.spacing(1),
        color: theme.palette.white,
        backgroundColor: theme.palette.error.light,
        '&:hover': {
            backgroundColor: theme.palette.error.main
        }
    },
    cloneButton: {
        marginLeft: theme.spacing(1),
        color: theme.palette.white,
        backgroundColor: colors.green[400],
        '&:hover': {
            backgroundColor: colors.green[700]
        }
    },
    errorIcon: {
        marginRight: theme.spacing(1),
        color: theme.palette.error.main,
    },
    marginBottom: {
        paddingLeft: theme.spacing(1),
        marginBottom: theme.spacing(3),
    },
    tabs: {
        marginTop: theme.spacing(3)
    },
    textField: {
        width: '100%',
    },
    cancelled: {
        borderRadius: 5,
        backgroundColor: red[100],
        '&:hover': {
            backgroundColor: red[200]
        }
    },
    chip: {
        backgroundColor: red[200]
    },
}));


export const PaymentEdit = (props: {
    payment: Payment,
    setSelectedPayment?: (payment: Payment | null) => void,
    className?: string,
    billingPartyId?: number,
    isAdmin: boolean,
    canEdit: boolean,
}) => {
    const classes = useStyles();
    const {payment, setSelectedPayment, className, billingPartyId, isAdmin, canEdit} = props;
    const isNewPayment = payment.id === -1;
    const billingPartyData = getBillingParty(!isNewPayment ? payment.billingPartyMember!.billing_party_id! : billingPartyId ? billingPartyId : -1);
    const [open, setOpen] = React.useState(false);
    const {enqueueSnackbar} = useSnackbar();
    const {history, location} = useRouter();
    const [sendCreatePayment, createData] = createPayment();
    const [sendUpdatePayment, updateData] = updatePayment();
    const [sendDeletePayment, deleteData] = deletePayment();
    const [seeProvider, setSeeProvider] = useState(false);

    //1. Set form and error state
    const [formState, setFormState] = useState({
        ...payment,
        provider_reference: payment.provider_reference ? payment.provider_reference : null,
        provider_status: payment.provider_status ? payment.provider_status : '',
        provider_response_raw: payment.provider_response_raw ? payment.provider_response_raw : '',
    });
    const loadingData = anyLoading(createData, updateData, deleteData, billingPartyData);
    const [errors, setErrors] = useState({
        isValid: false,
        totalsMismatch: false ? '' : undefined,
        errors: {},
        negativeMemberPayment: false ? '' : undefined,
    });
    const isRefund = formState.payment_type === PaymentType.REFUND_CHECK || formState.payment_type === PaymentType.REFUND_NEXIO;
    const isTransfer = formState.payment_type === PaymentType.TRANSFER;

    useEffect(() => {
        setFormState({
            ...payment,
            refunds_amount: payment.refunds_amount ? Math.abs(payment.refunds_amount) : 0,
            provider_reference: payment.provider_reference ? payment.provider_reference : null,
            provider_status: payment.provider_status ? payment.provider_status : '',
            provider_response_raw: payment.provider_response_raw ? payment.provider_response_raw : '',
        });
    }, [props.payment])

    //2. Setup helper functions for form
    const handleFieldChange = getHandleFieldChange(setFormState);
    const hasError = getHasError(errors);

    //3. setup validation and error hook
    const validateSchema: any = {
        timestamp: {
            presence: {allowEmpty: false, message: 'is required'},
        },
        amount: {
            numericality: {
                greaterThanOrEqualTo: 0,
                noStrings: true,
                message: 'must not be negative'
            }
        },
        billing_party_member_id: {
            numericality: {
                onlyInteger: true,
                greaterThan: 0,
                message: 'is required'
            }
        },
        refunds_amount: {
            numericality: {
                greaterThanOrEqualTo: 0,
                noStrings: true,
                message: 'must not be negative'
            }
        },
        billing_party_amount: {
            numericality: {
                greaterThanOrEqualTo: 0,
                noStrings: true,
                message: 'must not be negative'
            }
        },

    };

    useEffect(() => {
        const newErrors = validate(formState, validateSchema);
        const paymentsTotal = formState.groupMemberPayments!!.reduce(
            (total, memberPayment) => total + memberPayment.amount,
            (formState.billing_party_amount || 0) + (formState.refunds_amount || 0)
        );

        const totalsMismatch = isRefund ? false : (paymentsTotal !== formState.amount);
        const memberHasNegativeAmount = formState.groupMemberPayments!!.filter(payment => payment.amount < 0);
        const isValid = !newErrors && !totalsMismatch && !memberHasNegativeAmount.length;

        setErrors(state => ({
            ...state,
            isValid,
            totalsMismatch: totalsMismatch ? 'Group member amounts must match payment amount.' : undefined,
            negativeMemberPayment: memberHasNegativeAmount.length ? 'Member amounts can not be negative' : undefined,
            errors: newErrors || {},
        }));
    }, [formState]);

    //4. Handle save form
    const onSaveClick = () => {
        if (isNewPayment) {
            const create = formState.groupMemberPayments!.map(memberPayment => ({
                group_member_id: memberPayment.group_member_id,
                amount: memberPayment.amount,
            }));

            const sanitisedData: CreatePaymentInput = {
                data: {
                    ...{
                        ...formState,
                        id: undefined,
                        billing_party_member_id: formState.billing_party_member_id!,
                        amount: formState.amount!,
                        billing_party_amount: formState.billing_party_amount!,
                        timestamp: moment(formState.timestamp!).format('YYYY-MM-DD HH:mm:ss'),
                        payment_type: formState.payment_type!,
                        payment_status: formState.payment_status!,
                        description: formState.description!,
                        refunds_amount: formState.refunds_amount!,
                        groupMembers: undefined,
                        assignedAmount: undefined,
                        groupMemberPayments: {
                            create,
                        },
                        __typename: undefined,
                    },
                }
            };

            sendCreatePayment({variables: sanitisedData})
                .then(() => {
                    enqueueSnackbar('Payment Saved.', {variant: 'success'});
                    if (setSelectedPayment) setSelectedPayment(null);
                })
                .catch(() => {
                    enqueueSnackbar('Save Payment Failed!', {variant: 'error'});
                });
        } else {

            const createPayments = formState.groupMemberPayments!
                .filter(newMemberPayment => payment.groupMemberPayments!.find(memberPayment => memberPayment.group_member_id === newMemberPayment.group_member_id) === undefined)
                .map(memberPayment => ({
                    group_member_id: memberPayment.group_member_id,
                    amount: memberPayment.amount,
                }));
            const updatePayments = formState.groupMemberPayments!
                .filter(newMemberPayment => payment.groupMemberPayments!.find(memberPayment => memberPayment.group_member_id === newMemberPayment.group_member_id) !== undefined)
                .map(newMemberPayment => ({
                    id: payment.groupMemberPayments!.find(memberPayment => memberPayment.group_member_id === newMemberPayment.group_member_id)!.id,
                    group_member_id: newMemberPayment.group_member_id,
                    amount: newMemberPayment.amount,
                }));
            const deletePayments = payment.groupMemberPayments!
                .filter(existingPayment => formState.groupMemberPayments!.find(memberPayment => memberPayment.group_member_id === existingPayment.group_member_id) === undefined)
                .map(member => member.id);

            const sanitisedData: UpdatePaymentInput = {
                data: {
                    ...{
                        ...formState,
                        __typename: undefined,
                        billingPartyMember: undefined,
                        roomPartyMember: undefined,
                        groupMembers: undefined,
                        assignedAmount: undefined,
                        groupMemberPayments: {
                            create: createPayments,
                            update: updatePayments,
                            delete: deletePayments,
                        },
                        timestamp: moment(formState.timestamp!).format('YYYY-MM-DD HH:mm:ss'),
                    },
                }
            };

            sendUpdatePayment({variables: sanitisedData})
                .then(() => {
                    enqueueSnackbar('Payment Updated.', {variant: 'success'});
                    if (setSelectedPayment) setSelectedPayment(null);
                })
                .catch(() => {
                    enqueueSnackbar('Payment Update Failed!', {variant: 'error'});
                });
        }
    };

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleDelete = () => {
        sendDeletePayment({variables: {id: formState.id}})
            .then(() => {
                enqueueSnackbar('Payment Deleted.', {variant: 'success'});
                if (setSelectedPayment) setSelectedPayment(null);
            })
            .catch(() => {
                enqueueSnackbar('Deleted Payment Failed!', {variant: 'error'});
            });

        handleClose();
    };

    const billingParty = billingPartyData.data && billingPartyData.data.billingParty;
    const payingMembers = billingParty && billingParty.payingMembers;
    const payingMember = payingMembers && payingMembers.find(member => member.id === formState.billing_party_member_id);
    let groupMembers = billingParty && billingParty.members!.map(member => member.groupMember!);
    const isGroupLeader = payingMember && payingMember.is_group_leader;

    if (billingParty) {
        if (isGroupLeader || isTransfer){
            groupMembers = billingParty.group!.members!;
        } else {
            //add memebrs that are already being paid for that are cancelled.
            const extraMembers: GroupMember[] = payment.groupMemberPayments!
                .map(checkPayment => billingParty.group!.members!.find(member => member.id === checkPayment.group_member_id))
                .filter(member => member !== undefined)
                .filter(member => groupMembers ? groupMembers.find(checkMember => checkMember.id === member!.id) === undefined : true)
                .map(member => member!)
            ;

            groupMembers = groupMembers ? [...groupMembers, ...extraMembers] : extraMembers ? extraMembers : [];
        }
    }

    const showBillingPartyButton = location.pathname.indexOf('/billing_party/') === -1;
    const billingPartyAdjustmentsOutstanding = billingParty && billingParty.adjustments && billingParty.adjustments.reduce((total, item) => {
        return total + ((item.adjustment_type! === AdjustmentType.ADDITIONAL_COST || item.adjustment_type! === AdjustmentType.CREDIT_CARD_FEE) ? item.amount! : item.amount! * -1);
    }, 0);

    const billingPartyRefundsOutstanding = billingParty && billingParty.totalRefunds;

    const [openVoidModal, setOpenVoidModal] = React.useState(false);
    const handleOpenVoidModal = () => setOpenVoidModal(true);
    const handleCloseVoidModal = () => setOpenVoidModal(false);
    const handleVoidPayment = () => {
        const sanitisedData: UpdatePaymentInput = {
            data: {
                id: formState.id,
                payment_status: PaymentStatus.VOIDED,
            }
        };

        sendUpdatePayment({variables: sanitisedData})
            .then(() => {
                enqueueSnackbar('Payment Voided.', {variant: 'success'});
                if (setSelectedPayment) setSelectedPayment(null);
            })
            .catch(() => {
                enqueueSnackbar('Payment Void Failed!', {variant: 'error'});
            });
    };

    return (
        <Card className={clsx([className, classes.container])}>
            <CardHeader title="Edit Payment" action={showBillingPartyButton && billingParty &&
            <Button variant={'outlined'}
                    onClick={() => history.push('/billing_party/' + billingParty.id)}>Go to Billing Party</Button>
            }/>

            <form>
                <LoadingOverlay loading={loadingData.loading}>
                    <CardContent className={classes.card}>

                        <Grid container spacing={3}>

                            <Grid item xs={12} style={{display: 'flex'}}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12}>
                                        {isNewPayment && payingMembers && (
                                            <TextField
                                                {...hasError('billing_party_member_id')}
                                                fullWidth
                                                label="Billing Party Member"
                                                name="billing_party_member_id"
                                                onChange={handleFieldChange}
                                                select
                                                // eslint-disable-next-line react/jsx-sort-props
                                                SelectProps={{native: true}}
                                                value={formState.billing_party_member_id}
                                                variant="outlined"
                                                InputLabelProps={{
                                                    shrink: true,
                                                }}
                                                disabled={!canEdit}
                                            >
                                                <option value={-1}>please select...</option>
                                                {payingMembers.map(member => (
                                                    <option key={member.id} value={member.id}>
                                                        {member.first_name} {member.last_name} ({member.email})
                                                    </option>
                                                ))}
                                            </TextField>
                                        )}
                                        {!isNewPayment && payingMember && (
                                            <TextField
                                                fullWidth
                                                label="Billing Party Member"
                                                value={`${payingMember.first_name} ${payingMember.last_name} (${payingMember.email})`}
                                                disabled={true}
                                                variant="outlined"
                                            />
                                        )}
                                    </Grid>

                                    <Grid item md={3} sm={12} xs={12}>
                                        <TextField
                                            fullWidth
                                            type={'number'}
                                            label="amount"
                                            name="amount"
                                            variant="outlined"
                                            onChange={handleFieldChange}
                                            onFocus={event => event.target.select()}
                                            {...hasError('amount')}
                                            value={formState.amount}
                                            InputProps={{
                                                startAdornment: <InputAdornment
                                                    position="start">$</InputAdornment>,
                                                inputProps: {
                                                    min: 0,
                                                    onClick: (e) => e.currentTarget.select(),
                                                }
                                            }}
                                            disabled={!!formState.provider_response_raw || !canEdit}
                                        />
                                    </Grid>

                                    <Grid item  md={3} sm={12} xs={12}>
                                        <TextField
                                            {...hasError('payment_type')}
                                            fullWidth
                                            label="Payment Type"
                                            name="payment_type"
                                            onChange={handleFieldChange}
                                            select
                                            SelectProps={{native: true}}
                                            value={formState.payment_type}
                                            variant="outlined"
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            disabled={!!formState.provider_response_raw || !canEdit || (!isNewPayment && formState.payment_type === PaymentType.TRANSFER)}
                                        >
                                            {PaymentTypes.map((type, i) => (
                                                <option key={type.name} value={i}>
                                                    {type.name}
                                                </option>
                                            ))}
                                        </TextField>
                                    </Grid>

                                    <Grid item  md={3} sm={12} xs={12}>
                                        <TextField
                                            {...hasError('payment_status')}
                                            fullWidth
                                            label="Payment Status"
                                            name="payment_status"
                                            onChange={handleFieldChange}
                                            select
                                            // eslint-disable-next-line react/jsx-sort-props
                                            SelectProps={{native: true}}
                                            value={formState.payment_status}
                                            variant="outlined"
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            disabled={!!formState.provider_response_raw || !canEdit}
                                        >
                                            {PaymentStatuses.map((type, i) => (
                                                i !== PaymentStatus.REFUNDED && ( //TODO: put refinded back in when web hooks are fixed.
                                                    <option key={type.name} value={i}>
                                                        {type.name}
                                                    </option>
                                                )
                                            ))}
                                        </TextField>
                                    </Grid>

                                    {isAdmin && (
                                        <Grid item  md={3} sm={12} xs={12}>
                                            <Grid component="label" container alignItems="center" spacing={1}>
                                                <Grid item>
                                                    <Switch
                                                        checked={formState.reconciled}
                                                        color="secondary"
                                                        edge="start"
                                                        name="reconciled"
                                                        onChange={handleFieldChange}
                                                        value={formState.reconciled}
                                                        disabled={!canEdit}
                                                    />
                                                </Grid>
                                                <Grid item><Typography variant="h5">Reconciled?</Typography></Grid>
                                            </Grid>
                                        </Grid>
                                    )}

                                    {isAdmin && (
                                        <Grid item xs={12}>
                                            <Grid component="label" container alignItems="center" spacing={1}>
                                                <Grid item>
                                                    <Switch
                                                        checked={seeProvider}
                                                        color="secondary"
                                                        edge="start"
                                                        onChange={(event, checked) => setSeeProvider(checked)}
                                                        value={seeProvider}
                                                        disabled={!canEdit}
                                                    />
                                                </Grid>
                                                <Grid item><Typography variant="h5">Show Provider Reference</Typography></Grid>
                                            </Grid>
                                        </Grid>
                                    )}

                                    {isAdmin && (
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={2}
                                                label="Description"
                                                name="description"
                                                onChange={handleFieldChange}
                                                value={formState.description}
                                                variant="outlined"
                                                disabled={!canEdit}
                                            />
                                        </Grid>
                                    )}

                                    {seeProvider && (
                                        <React.Fragment>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    label="provider_reference"
                                                    name="provider_reference"
                                                    variant="outlined"
                                                    value={formState.provider_reference}
                                                    disabled={true}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    label="provider_status"
                                                    name="provider_status"
                                                    variant="outlined"
                                                    value={formState.provider_status}
                                                    disabled={true}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    multiline
                                                    rows={3}
                                                    label="provider_response_raw"
                                                    name="provider_response_raw"
                                                    variant="outlined"
                                                    value={formState.provider_response_raw}
                                                    disabled={true}
                                                />
                                            </Grid>
                                        </React.Fragment>
                                    )}


                                    {canEdit && !isRefund && (
                                        <React.Fragment>

                                            <Grid item xs={12}>
                                                <Typography variant="body1">
                                                    Please select where this payment is to be allocated:
                                                </Typography>
                                                {errors.totalsMismatch && (<Typography variant="body1"
                                                                                       color={'error'}>{errors.totalsMismatch}</Typography>)}
                                                {errors.negativeMemberPayment && (<Typography variant="body1" color={'error'}>{errors.negativeMemberPayment}</Typography>)}
                                            </Grid>


                                            {!!billingPartyAdjustmentsOutstanding && billingPartyAdjustmentsOutstanding > 0 && (
                                                <Grid item xs={12}>
                                                    <Grid component="label" container alignItems="center" spacing={1}>
                                                        <Grid item xs={12}><Typography variant="body1">Billing Party
                                                            Adjustments</Typography></Grid>
                                                        <Grid item xs={12}>
                                                            <TextField
                                                                fullWidth
                                                                label="Amount"
                                                                variant="outlined"
                                                                name="billing_party_amount"
                                                                onChange={handleFieldChange}
                                                                onFocus={event => event.target.select()}
                                                                {...hasError('billing_party_amount')}
                                                                value={formState.billing_party_amount}
                                                                InputProps={{
                                                                    startAdornment: <InputAdornment
                                                                        position="start">$</InputAdornment>,
                                                                    inputProps: {
                                                                        onClick: (e) => e.currentTarget.select(),
                                                                        min: 0,
                                                                        max: billingPartyAdjustmentsOutstanding ? billingPartyAdjustmentsOutstanding : 0,
                                                                    }
                                                                }}
                                                                type="number"
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12}>
                                                            <Typography
                                                                variant="body1">Outstanding: ${billingPartyAdjustmentsOutstanding}</Typography>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            )}

                                            {!!billingPartyRefundsOutstanding && billingPartyRefundsOutstanding > 0 && (
                                                <Grid item xs={12}>
                                                    <Grid component="label" container alignItems="center" spacing={1}>
                                                        <Grid item xs={12}><Typography variant="body1">Allocate to
                                                            Refunds</Typography></Grid>
                                                        <Grid item xs={12}>
                                                            <TextField
                                                                fullWidth
                                                                label="Amount"
                                                                name="refunds_amount"
                                                                variant="outlined"
                                                                onChange={handleFieldChange}
                                                                onFocus={event => event.target.select()}
                                                                {...hasError('refunds_amount')}
                                                                value={formState.refunds_amount}
                                                                InputProps={{
                                                                    startAdornment: <InputAdornment
                                                                        position="start">$</InputAdornment>,
                                                                    inputProps: {
                                                                        onClick: (e) => e.currentTarget.select(),
                                                                        min: 0,
                                                                        max: billingPartyRefundsOutstanding ? billingPartyRefundsOutstanding : 0,
                                                                    }
                                                                }}
                                                                type="number"
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12}>
                                                            <Typography
                                                                variant="body1">Outstanding: ${billingPartyRefundsOutstanding}</Typography>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            )}

                                            {groupMembers && groupMembers.map(groupMember => {
                                                const memberPayment = groupMember && formState.groupMemberPayments!.find(checkPayment => checkPayment.group_member_id === groupMember.id);
                                                const isChecked = !!memberPayment;

                                                return groupMember && (
                                                    <React.Fragment>
                                                    <Grid key={groupMember.id} item xs={12}
                                                          className={groupMember.cancelled ? classes.cancelled : ''}>
                                                        <Grid component="label" container alignItems="center"
                                                              spacing={1}>
                                                            <Grid item sm={1} xs={3}>
                                                                <Checkbox
                                                                    onChange={(event, checked) => {
                                                                        setFormState({
                                                                            ...formState,
                                                                            groupMemberPayments: !checked ?
                                                                                formState.groupMemberPayments!.filter(checkPayment => checkPayment.group_member_id !== groupMember.id) :
                                                                                [...formState.groupMemberPayments!, {
                                                                                    id: -1,
                                                                                    payment_id: payment.id,
                                                                                    group_member_id: groupMember.id,
                                                                                    amount: 0,
                                                                                }]
                                                                        });
                                                                    }}
                                                                    checked={isChecked}
                                                                />
                                                            </Grid>
                                                            <Grid item sm={11} xs={9}><Typography
                                                                variant="body1">{groupMember.first_name + ' ' + groupMember.last_name}</Typography></Grid>
                                                        </Grid>
                                                    </Grid>
                                                            {memberPayment && (
                                                                <Grid item xs={12}>
                                                                    <TextField
                                                                        fullWidth
                                                                        label="amount"
                                                                        name="member_amount"
                                                                        variant="outlined"
                                                                        value={memberPayment.amount}
                                                                        InputProps={{
                                                                            startAdornment: <InputAdornment
                                                                                position="start">$</InputAdornment>,
                                                                            inputProps: {
                                                                                onClick: (e) => e.currentTarget.select(),
                                                                                min: 0,
                                                                                max: groupMember.roomMember && groupMember.roomMember.outstandingBalance ? groupMember.roomMember.outstandingBalance : 0,
                                                                            }
                                                                        }}
                                                                        onChange={event => {
                                                                            setFormState({
                                                                                ...formState,
                                                                                groupMemberPayments:
                                                                                    formState.groupMemberPayments!.map(checkPayment =>
                                                                                        (checkPayment.group_member_id === groupMember.id) ?
                                                                                            {
                                                                                                ...checkPayment,
                                                                                                amount: Number(event.target.value)
                                                                                            } :
                                                                                            checkPayment
                                                                                    )
                                                                            });
                                                                        }}
                                                                        onFocus={event => event.target.select()}
                                                                        type="number"
                                                                    />
                                                                </Grid>
                                                            )}
                                                            {groupMember.cancelled && (
                                                                <Grid item xs={12}>
                                                                    <Chip icon={<BlockOutlinedIcon/>} label="cancelled"
                                                                          className={classes.chip} size="small"/>
                                                                </Grid>
                                                            )}
                                                            {!groupMember.cancelled && groupMember.roomMember && (
                                                                <Grid item xs={12}>
                                                                    <Typography
                                                                        variant="body1">Outstanding: ${groupMember.roomMember.outstandingBalance}</Typography>
                                                                </Grid>
                                                            )}
                                                    </React.Fragment>
                                                )
                                            })}
                                        </React.Fragment>
                                    )}

                                </Grid>
                            </Grid>

                            <Grid item xs={12}>
                                {isAdmin && (
                                    <Button className={classes.saveButton} onClick={onSaveClick} variant="contained"
                                            disabled={!errors.isValid}>Save</Button>
                                )}
                                {isAdmin && (
                                    <Can permission="payments.delete">
                                        <Button onClick={handleClickOpen} className={classes.deleteButton}
                                                variant="contained"
                                                disabled={isNewPayment}>
                                            Delete
                                        </Button>
                                    </Can>
                                )}
                                {isAdmin && (
                                    <Can permission="payments.delete">
                                        <Button onClick={handleOpenVoidModal} className={classes.deleteButton}
                                                variant="contained"
                                                disabled={isNewPayment}>
                                            Void
                                        </Button>
                                    </Can>
                                )}

                                {setSelectedPayment && (
                                    <Button variant={'outlined'} style={{marginLeft: 10}}
                                            onClick={() => setSelectedPayment(null)}>Close</Button>
                                )}
                            </Grid>

                        </Grid>
                    </CardContent>
                </LoadingOverlay>

                <ConfirmDelete onClose={handleClose} onDeleteClick={handleDelete} open={open}/>

                {openVoidModal && (
                    <ConfirmModal
                        onClose={handleCloseVoidModal}
                        onConfirmClick={() => {
                            handleVoidPayment();
                            handleCloseVoidModal();
                        }}
                        open={true}
                        title="Void Payment"
                        message="Would you like to void this payment?"
                    />
                )}
            </form>
        </Card>

    );
};

export default PaymentEdit;
